import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Button,
  FormLabel,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";

const PaymentRecords = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("");

  // Top cards data
  const topCardsData = [
    {
      id: 1,
      value: "156",
      label: "Total Payments",
      icon: "solar:dollar-outline",
      iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
    {
      id: 2,
      value: "$45,230",
      label: "Total Received",
      icon: "streamline:bag-dollar",
      iconColor: "#23A26D",
      bgColor: "#E8F5E9",
    },
    {
      id: 3,
      value: "142",
      label: "Applied",
      icon: "simple-line-icons:check",
      iconColor: "#525866",
      bgColor: "#7C8DB51F",
    },
    {
      id: 4,
      value: "9",
      label: "Pending",
      icon: "material-symbols:pending-outline",
      iconColor: "#d32f2f",
      bgColor: "#ffebee",
    },
  ];

  // Payment records data
  const paymentRecords = [
    {
      id: "PAY-2024-001",
      customer: "ABC Construction Corp",
      invoice: "AST-2024-001",
      qbNumber: "INV-2024-001",
      amount: "$12,500.00",
      lastSync: "2 hours ago",
      dueDate: "2/14/2024",
      paymentDetails: {
        method: "Check",
        checkNumber: "1234",
        amount: "$12,500.00",
      },
      received: "1/15/2024",
      processed: "2/14/2024",
      qbStatus: "Applied",
    },
    {
      id: "PAY-2024-002",
      customer: "XYZ Builders Inc",
      invoice: "AST-2024-002",
      qbNumber: "INV-2024-002",
      amount: "$8,750.00",
      lastSync: "2 hours ago",
      dueDate: "2/15/2024",
      paymentDetails: {
        method: "Bank Transfer",
        checkNumber: "",
        amount: "$8,750.00",
      },
      received: "1/16/2024",
      processed: "2/15/2024",
      qbStatus: "Error",
    },
    {
      id: "PAY-2024-003",
      customer: "Metro Developers LLC",
      invoice: "AST-2024-003",
      qbNumber: "INV-2024-003",
      amount: "$5,200.00",
      lastSync: "2 hours ago",
      dueDate: "2/20/2024",
      paymentDetails: {
        method: "Credit Card",
        checkNumber: "",
        amount: "$5,200.00",
      },
      received: "1/18/2024",
      processed: "Pending",
      qbStatus: "Pending",
    },
  ];

  // Status chip styling and color mapping
  const getStatusConfig = (status) => {
    const statusConfig = {
      Applied: {
        color: "#E8F5E9",
        textColor: "#23A26D",
        label: "Applied",
        amountColor: "#23A26D",
      },
      Pending: {
        color: "#FFF3E0",
        textColor: "#DD992F",
        label: "Pending",
        amountColor: "#DD992F",
      },
      Error: {
        color: "#FFEBEE",
        textColor: "#D80027",
        label: "Error",
        amountColor: "#D80027",
      },
    };

    return (
      statusConfig[status] || {
        color: "#f0f0f1",
        textColor: "#666",
        label: status,
        amountColor: "#666",
      }
    );
  };

  const getStatusChip = (status) => {
    const config = getStatusConfig(status);

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: config.textColor,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
          minWidth: 80,
        }}
      />
    );
  };

  const getAmountColor = (status) => {
    const config = getStatusConfig(status);
    return config.amountColor;
  };

  const handlePageBack = () => {
    navigate("/quickbooks");
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Quickbooks/Payment"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        {/* Single Main Card containing everything */}
        <Card
          sx={{
            // borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header and Sync Now Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
                mb: 4,
              }}
            >
              <Button
                variant="contained"
                color="main"
                size="medium"
                startIcon={
                  <Icon icon="heroicons:arrow-path" width={20} height={20} />
                }
              >
                Sync Now
              </Button>
            </Box>

            {/* Top Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {topCardsData.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 160,
                      }}
                    >
                      {/* Icon on the left */}
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {card.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Card
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {/* Header on the left */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      Payment Records
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Payments logged in Cleri automatically update invoice
                      status in QuickBooks
                    </Typography>
                  </Box>

                  {/* Search and Filters on the right */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 4,
                    }}
                  >
                    <CustomTextField
                      placeholder="Search"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                     sx={{
                        width: 200,
                        "& .MuiOutlinedInput-root": {
                          height: "28px",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon icon="tabler:search" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                        </MenuItem>
                        <MenuItem value="Applied">Applied</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Error">Error</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={paymentTypeFilter}
                        onChange={(e) => setPaymentTypeFilter(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <Typography variant="body2" color="text.secondary">
                            Payment Type
                          </Typography>
                        </MenuItem>
                        <MenuItem value="Check">Check</MenuItem>
                        <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                        <MenuItem value="Credit Card">Credit Card</MenuItem>
                        <MenuItem value="Wire Transfer">Wire Transfer</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Payment Records List */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {paymentRecords.map((payment, index) => (
                    <Card
                      key={payment.id}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        overflow: "visible",
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        {/* Payment Header */}
                        <Box
                          sx={{
                            p: 3,
                            pb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                              p: 2,
                            }}
                          >
                            <Typography variant="h5">{payment.id}</Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 2,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: getAmountColor(payment.qbStatus),
                                }}
                              >
                                {payment.amount}
                              </Typography>
                              {getStatusChip(payment.qbStatus)}
                            </Box>
                          </Box>
                          <Card
                            sx={{
                              p: 3,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "#003A700D",
                              backgroundColor: "#003A700D",
                              mb: 0,
                            }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} md={2}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  Customer
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 560 }}
                                >
                                  {payment.customer}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={2}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  Invoice
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 560 }}
                                >
                                  {payment.invoice}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={2}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  QB Number
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 560 }}
                                >
                                  {payment.qbNumber}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={2}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  Last Sync
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 560 }}
                                >
                                  {payment.lastSync}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={2}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  Due Date
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 560 }}
                                >
                                  {payment.dueDate}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Card>
                        </Box>

                        {/* Payment Details Section */}
                        {/* Payment Details Section */}
                        <Box sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 3,
                              flexDirection: { xs: "column", md: "row" },
                            }}
                          >
                            {/* Payment Details Box */}
                            <Card
                              sx={{
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "#97A4B10D",
                                backgroundColor: "#97A4B10D",
                                flex: 1,
                                minWidth: { xs: "auto", md: 300 },
                              }}
                            >
                              <CardContent sx={{ p: 3 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                  }}
                                >
                                  <Typography variant="h6" sx={{ mb: 2 }}>
                                    Payment Details
                                  </Typography>

                                  {/* Method Row */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontWeight: 600, minWidth: 80 }}
                                    >
                                      Method:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        flex: 1,
                                        textAlign: "right",
                                      }}
                                    >
                                      {payment.paymentDetails.method}
                                    </Typography>
                                  </Box>

                                  {/* Check Number Row (if exists) */}
                                  {payment.paymentDetails.checkNumber && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600, minWidth: 80 }}
                                      >
                                        Check #
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 500,
                                          flex: 1,
                                          textAlign: "right",
                                        }}
                                      >
                                        {payment.paymentDetails.checkNumber}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Amount Row */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontWeight: 600, minWidth: 80 }}
                                    >
                                      Amount:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        flex: 1,
                                        textAlign: "right",
                                        color: getAmountColor(payment.qbStatus),
                                      }}
                                    >
                                      {payment.paymentDetails.amount}
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>

                            {/* Status Information Box */}
                            <Card
                              sx={{
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "#97A4B10D",
                                backgroundColor: "#97A4B10D",
                                flex: 1,
                                minWidth: { xs: "auto", md: 300 },
                              }}
                            >
                              <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                  Status Information
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flex: 1,
                                      }}
                                    >
                                      <Icon
                                        icon="uit:calender"
                                        width={16}
                                        height={16}
                                        style={{ color: "#23A26D" }}
                                      />
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600, minWidth: 80 }}
                                      >
                                        Received:
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        textAlign: "right",
                                      }}
                                    >
                                      {payment.received}
                                    </Typography>
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flex: 1,
                                      }}
                                    >
                                      <Icon
                                        icon="uit:calender"
                                        width={16}
                                        height={16}
                                        style={{ color: "#D80027" }}
                                      />
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600, minWidth: 80 }}
                                      >
                                        Processed:
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        textAlign: "right",
                                      }}
                                    >
                                      {payment.processed}
                                    </Typography>
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flex: 1,
                                      }}
                                    >
                                      <Icon
                                        icon="fluent:status-20-regular"
                                        width={16}
                                        height={16}
                                        style={{ color: "#003A70" }}
                                      />
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600, minWidth: 80 }}
                                      >
                                        QB Status:
                                      </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: "right" }}>
                                      {getStatusChip(payment.qbStatus)}
                                    </Box>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Card>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </UserLayout>
  );
};

export default PaymentRecords;
